import React, { useEffect, useState, useCallback } from 'react';
import { Field, change } from 'redux-form';
import { useDispatch, useSelector } from 'react-redux';
import * as validate from '@app/src/utils/validations';
import { FieldForm } from '@app/src/common/FieldForm';
import { renderInput } from '@app/src/common/Input';
import { Modal } from '@app/src/common/Modal';
import { Icon } from '@app/src/common/Icon';
import {
  BillWrapper,
  CentralHelp,
  LinkHelp,
  FieldWrapper,
  Label,
  IconInfo,
  ModalContent
} from './styled';
import {
  fetchRuc,
  setDisabledRucInputs,
  clearRucData,
  fetchRucSuccess
} from '@app/src/views/StepPayment/formPayment';
import { DataListModel } from '@app/src/common/PredictiveInput/DataListModel';
import { renderPredictive } from '@app/src/common/PredictiveInput';
import { fetchDistrictAction, setDistrictFullData, clearDistrictFullData } from '@app/src/views/StepOne/form';
import { isClientEnv } from '@app/src/utils/validations';
import { maxLength } from '@app/src/utils/normalize';

const isClient = isClientEnv();

interface Props {
  color: string;
}

export const BillProfile: React.FC<Props> = ({ color }) => {
  const dispatch = useDispatch();
  const currentRuc = useSelector(state => state.form.stepPayment.values.ruc);
  const currentBusinessName = useSelector(state => state.form.stepPayment.values.rznSocial);
  const currentName = useSelector(state => state.form.stepPayment.values.businessName);
  const currentFatherLastName = useSelector(state => state.form.stepPayment.values.businessFatherLastName);
  const currentMotherLastName = useSelector(state => state.form.stepPayment.values.businessMotherLastName);
  const currentAddress = useSelector(state => state.form.stepPayment.values.businessAddress);
  const rucData = useSelector(state => state.ruc.data);
  const isLoading = useSelector(state => state.ruc.isFetching);
  const [isShowRazSocialInput, setIsShowRazSocialInput] = useState(false);
  const [isDisabledContact, setIsDisabledContact] = useState(false);
  const isDisabledInputs = useSelector(state => state.ruc.isDisabledInputs);
  const currentPhone = useSelector(state => state.form.stepPayment.values.businessPhone);
  const currentEmail = useSelector(state => state.form.stepPayment.values.businessEmail);
  const districts: DataListModel[] = useSelector(state => state.district.data);
  const currentUbigeo = useSelector(state => state.district.ubigeo);
  const isLoadingUbigeo = useSelector(state => state.district.isFetching);
  const [isShowInfo, setIsShowInfo] = useState(false);

  useEffect(() => {
    if (districts && districts.length === 0) {
      if (isClient && window.innerWidth < 768) {
        dispatch(fetchDistrictAction('lim'));
      }
    }
  }, [districts])

  useEffect(() => {
    dispatch(change('stepPayment', 'rznSocial', ''));
    dispatch(change('stepPayment', 'businessName', ''));
    dispatch(change('stepPayment', 'businessFatherLastName', ''));
    dispatch(change('stepPayment', 'businessMotherLastName', ''));
    dispatch(change('stepPayment', 'businessAddress', ''));
    dispatch(change('stepPayment', 'businessPhone', ''));
    dispatch(change('stepPayment', 'businessEmail', ''));
    dispatch(clearDistrictFullData());
    dispatch(change('stepPayment', 'districtId', ''));
    dispatch(change('stepPayment', 'departmentId', ''));
    dispatch(change('stepPayment', 'location', ''));
    dispatch(setDisabledRucInputs(false));
    dispatch(clearRucData());
    setIsShowRazSocialInput(false);
    setIsDisabledContact(false);
    if (currentRuc && currentRuc.charAt(0) === '2') {
      setIsShowRazSocialInput(true);
    }
  }, [currentRuc]);

  useEffect(() => {
    if(Object.keys(rucData).length > 0 && currentRuc === rucData.documentNumber) {
      dispatch(change('stepPayment', 'rznSocial', rucData.businessName));
      dispatch(change('stepPayment', 'businessName', rucData.name));
      dispatch(change('stepPayment', 'businessFatherLastName', rucData.fatherLastName));
      dispatch(change('stepPayment', 'businessMotherLastName', rucData.motherLastName));
      dispatch(change('stepPayment', 'businessAddress', rucData.address));
      dispatch(change('stepPayment', 'businessPhone', rucData.phone));
      dispatch(change('stepPayment', 'businessEmail', rucData.email));
      dispatch(setDistrictFullData(rucData.location));
      dispatch(change('stepPayment', 'districtId', rucData.districtId));
      dispatch(change('stepPayment', 'departmentId', rucData.deparmentId));
      dispatch(change('stepPayment', 'location', rucData.location));
      dispatch(fetchRucSuccess(rucData));
      dispatch(setDisabledRucInputs(true));
      if (currentRuc && currentRuc.charAt(0) === '2') {
        setIsDisabledContact(false);
      } else {
        setIsDisabledContact(true);
      }
    }
  }, [rucData, currentRuc]);

  const handleChangeRuc = useCallback((value: string) => {
    if (value && value.length === 11) {
      dispatch(fetchRuc(Number(value)));
    }
  }, [dispatch])

  const handleChangePredictive = (data: DataListModel) => {
    if (data && data.data) {
      const { location, district_adecsys, department_adecsys } = data.data;
      dispatch(setDistrictFullData(location));
      dispatch(change('stepPayment', 'location', location));
      dispatch(change('stepPayment', 'districtId', district_adecsys));
      dispatch(change('stepPayment', 'departmentId', department_adecsys));
    }
  };

  const handleBlurPredictive = () => {
    if (!currentUbigeo) {
      dispatch(change('stepPayment', 'location', ''));
      dispatch(change('stepPayment', 'districtId', ''));
      dispatch(change('stepPayment', 'departmentId', ''));
    }
  };

  const handleClickClose = () => {
    dispatch(change('stepPayment', 'location', ''));
    dispatch(change('stepPayment', 'districtId', ''));
    dispatch(change('stepPayment', 'departmentId', ''));
    dispatch(clearDistrictFullData());
  }

  const handleCloseModal = useCallback(() => {
    setIsShowInfo(false);
   }, [dispatch])
 
   const handleClickInfo = useCallback(() => {
     setIsShowInfo(true);
   }, [dispatch])

  return(
  <React.Fragment>
  <BillWrapper>
    <FieldForm label="N° de RUC">
      <Field
        name="ruc"
        type="number"
        placeholder="Ingrese RUC"
        component={renderInput}
        handleChange={handleChangeRuc}
        normalize={maxLength(11)}
        validate={[validate.required, validate.ruc]}
        loading={isLoading}
        disabled={isLoading}
        defaultValue={currentRuc}
        />
    </FieldForm>
    {
      isShowRazSocialInput &&
        <FieldForm label="Razón Social" className="rznSocial">
          <Field
            name="rznSocial"
            placeholder="Ingrese razón social"
            component={renderInput}
            maxLength={80}
            validate={[validate.required]}
            defaultValue={currentBusinessName}
            disabled={isDisabledInputs || isLoading}
          />
        </FieldForm>
    }
    <FieldForm label="Distrito" className="district">
      <Field
        name="location"
        dataList={districts}
        defaultUbigeo={currentUbigeo}
        handleChange={handleChangePredictive}
        handleBlur={handleBlurPredictive}
        component={renderPredictive}
        validate={isDisabledInputs ? null : [validate.required]}
        handleClickClose={handleClickClose}
        disabled={isDisabledInputs || isLoading }
        isLoading={isLoadingUbigeo}
        onDemand
      />
    </FieldForm>
    <FieldForm label="Dirección" className="address">
      <Field
        name="businessAddress"
        placeholder="Ingrese dirección"
        component={renderInput}
        maxLength={40}
        validate={[validate.required]}
        defaultValue={currentAddress}
        disabled={isDisabledInputs || isLoading}
      />
    </FieldForm>
  </BillWrapper>
  <CentralHelp>Para cualquier consulta o modificación acerca de los datos de facturación contactarse con la <LinkHelp color={color} target="_blank" href="https://centraldeayuda.clasificados.pe/hc/es/requests/new">Central de ayuda</LinkHelp>.</CentralHelp>
  <Label>Datos de Contacto</Label>
  <FieldWrapper>
    <FieldForm label="Nombres">
      <Field
        name="businessName"
        placeholder="Ingrese nombres"
        component={renderInput}
        maxLength={40}
        validate={[validate.required, validate.minLength3, validate.onlyLetters]}
        defaultValue={currentName}
        disabled={isLoading || isDisabledContact}
      />
    </FieldForm>
    <FieldForm label="Apellido paterno">
      <Field
        name="businessFatherLastName"
        placeholder="Ingrese apellidos"
        component={renderInput}
        maxLength={40}
        validate={[validate.required, validate.minLength3, validate.onlyLetters]}
        defaultValue={currentFatherLastName}
        disabled={ isLoading || isDisabledContact}
      />
    </FieldForm>
    <FieldForm label="Apellido materno">
      <Field
        name="businessMotherLastName"
        placeholder="Ingrese apellidos"
        component={renderInput}
        maxLength={40}
        validate={[validate.required, validate.minLength3, validate.onlyLetters]}
        defaultValue={currentMotherLastName}
        disabled={ isLoading || isDisabledContact}
      />
    </FieldForm>
      <FieldForm label="Teléfono">
        <Field
          name="businessPhone"
          type="number"
          component={renderInput}
          normalize={maxLength(11)}
          validate={[validate.required, validate.minLength6]}
          placeholder="Ingrese teléfono"
          defaultValue={currentPhone}
          disabled={isLoading}
        />
      </FieldForm>
      <FieldForm
        componentRight={
          <IconInfo onClick={handleClickInfo}>
            <Icon data-name="icon-info" fill={color} width="12" height="16" />
          </IconInfo>
        }
        label="Correo electrónico">
        <Field
          name="businessEmail"
          component={renderInput}
          maxLength={80}
          validate={[validate.required, validate.email]}
          placeholder="Ingrese correo electrónico"
          defaultValue={currentEmail}
          disabled={isLoading}
        />
      </FieldForm>
    </FieldWrapper>
    <Modal show={isShowInfo} closeModal={handleCloseModal}>
      <ModalContent>
        Registra tu correo para recibir las notificaciones de <strong>Clasificados.pe</strong>.
      </ModalContent>
    </Modal>
  </React.Fragment>
)};
